import React, { useEffect } from "react";
import {
	ProductPricingPlan,
	ProductBillingType,
	productBillingTypeOptions
} from "@/Contracts/Products";
import { createGuid } from "@/Contracts/Utils";
import { getCurrencySettings } from "@/Utils/Currency";
import {
	handleMarkupMargin,
	handleMarkupChange,
	handleMarginChange
} from "@/Components/Utils/PricingCalculator";
import { GlobalState } from "@/Reducers/interface";
import { useSelector } from "react-redux";
import { IPricingPlanProps } from "./interface";
import Form from "antd/lib/form";
import ZForm from "@organisms/ZForm";
import {
	IFormProps,
	IFormSectionProps,
	EWidgets,
	IFormFieldProps,
	ICustomFormItemProps
} from "@organisms/ZForm/interface";
import ZDrawer from "@molecules/ZDrawer";
import Skeleton from "antd/lib/skeleton";
import useDispatch from "@/Components/Utils/useDispatch";
import SettingsActionCreators from "@/Actions/Settings";
import { useFeatureFlag } from "@/Hooks/useFeature";
import ZSupplierSelect from "@organisms/ZSupplierSelect";
import CatalogCurrencyHint from "@molecules/CatalogCurrencyHint";

const PricingPlanDrawer: React.FC<IPricingPlanProps> = ({
	visible,
	isLoading,
	onSubmit,
	onClose,
	currentActiveProductPricingPlan = null,
	isEditMode = false,
	supplierId
}) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const {
		defaultPreferences: oldPreferences,
		suppliers,
		companyPreferences
	} = useSelector((state: GlobalState) => ({
		suppliers: state.settings.suppliers,
		companyPreferences:
			state.user.userCompany.settings.preference_settings
				?.product_preference_settings,
		defaultPreferences:
			state.user.userData?.user_settings?.preference_settings
				?.product_preference_settings
	}));

	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");

	const defaultPreferences = movePreferencesToGlobal
		? companyPreferences
		: oldPreferences;

	useEffect(() => {
		currentActiveProductPricingPlan?.sell_price &&
			populateMarkupMargin(currentActiveProductPricingPlan?.sell_price);
	}, [currentActiveProductPricingPlan?.sell_price]);

	useEffect(() => {
		form.resetFields();
	}, [visible]);

	useEffect(() => {
		form.setFieldsValue({ supplier_id: supplierId });
	}, [supplierId]);

	const handleAddSubmit = (addProductPricingPlan: ProductPricingPlan) => {
		addProductPricingPlan.session_id = createGuid();
		onSubmit(addProductPricingPlan);
	};

	const handleEditSubmit = (editProductPricingPlan: ProductPricingPlan) => {
		editProductPricingPlan.id = currentActiveProductPricingPlan?.id ?? null;
		editProductPricingPlan.item_id =
			currentActiveProductPricingPlan?.item_id ?? null;
		editProductPricingPlan.session_id =
			currentActiveProductPricingPlan?.session_id ?? null;
		onSubmit(editProductPricingPlan);
	};

	const populateMarkupMargin = (sellPrice: number, firstRender?: boolean) => {
		if (firstRender && currentActiveProductPricingPlan) {
			return handleMarkupMargin(
				currentActiveProductPricingPlan.cost_price,
				currentActiveProductPricingPlan.sell_price,
				1,
				false,
				currentActiveProductPricingPlan.setup_price
			);
		}
		const values = getFieldValues();
		if (!values) return;
		const response = handleMarkupMargin(
			values.cost_price,
			sellPrice,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.setup_price
		);
		return response;
	};

	const getFieldValues = () => {
		return form.getFieldsValue([
			"cost_price",
			"sell_price",
			"should_add_setup_price_to_margin",
			"setup_price",
			"margin",
			"markup",
			"vendor_name",
			"vendor_sku_no",
			"asin"
		]);
	};

	const onCostPriceChange = (newCostPrice?: number | string | null): void => {
		if (newCostPrice === undefined || newCostPrice === null) {
			return;
		}
		const values = getFieldValues();
		form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onMarkupChange = (newMarkup?: number | string | null): void => {
		if (newMarkup === undefined || form.getFieldValue("cost_price") === 0) {
			return;
		}
		const values = getFieldValues();
		form.setFieldsValue(handleMarkupChange(values, Number(newMarkup)));
	};

	const onMarginChange = (newMargin?: number | string | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			form.getFieldValue("cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();

		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().cost_price) !== 0
		) {
			return;
		}
		form.setFieldsValue(handleMarginChange(values, Number(newMargin)));
	};

	const onSellPriceChange = (newSellPrice?: number | string | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.setup_price
			)
		);
	};

	const onSetupPriceChange = (
		newSetupPrice?: number | string | null
	): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	const showAddSupplierDrawer = () =>
		dispatch(SettingsActionCreators.showAddOrEditSupplierDrawer());

	const formFields: IFormFieldProps[] = [
		{
			key: "name",
			label: "Name",
			hasFeedback: true,
			required: true,
			message: "Please enter pricing plan name",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter pricing plan name"
			},
			rules: [
				{
					whitespace: true,
					message: "Pricing plan name is required"
				}
			]
		},
		{
			key: "description",
			label: "Description",
			widget: EWidgets.INPUTTEXTAREA,
			hasFeedback: true,
			widgetProps: {
				placeholder: "Please enter pricing plan description",
				autoSize: { minRows: 3, maxRows: 3 },
				allowClear: true
			}
		},
		{
			key: "billing_period",
			label: "Billing Period",
			required: true,
			message: "Please select billing period",
			hasFeedback: true,
			widget: EWidgets.SELECT,
			widgetProps: {
				placeholder: "Please select billing period",
				selectOptions: (
					Object.keys(
						productBillingTypeOptions
					) as ProductBillingType[]
				).map((value, index) => ({
					name: productBillingTypeOptions[value],
					value,
					key: index
				}))
			}
		},
		{
			key: "cost_price",
			label: "Cost Price",
			hasFeedback: true,
			rules: [
				{
					required: true,
					validator: (_: any, value: any) => {
						if (Number(value) < 0) {
							return Promise.reject(
								new Error(
									"Invalid Entry. Cost price cannot be less than 0"
								)
							);
						}
						if (!value && value !== 0) {
							return Promise.reject(
								new Error("Cost price cannot be empty")
							);
						}
						return Promise.resolve();
					}
				}
			],
			widget: EWidgets.INPUTNUMBER,
			widgetProps: {
				width: "full",
				placeholder: "Please enter cost price",
				precision: 2,
				step: 0.01,
				min: 0,
				onChange: onCostPriceChange,
				addOnAfter: getCurrencySettings().currency
			}
		},
		{
			key: "markup",
			label: "Markup",
			hasFeedback: true,
			widget: EWidgets.INPUTNUMBER,
			widgetProps: {
				width: "full",
				placeholder: "Please enter product Markup",
				precision: 2,
				step: 0.01,
				max: 100,
				onChange: onMarkupChange,
				addOnAfter: "%"
			}
		},
		{
			key: "margin",
			label: "Margin",
			hasFeedback: true,
			widget: EWidgets.INPUTNUMBER,
			widgetProps: {
				width: "full",
				placeholder: "Please enter product Markup",
				precision: 2,
				step: 0.01,
				max: 100,
				onChange: onMarginChange,
				addOnAfter: "%"
			},
			rules: [
				{
					validator: (_: any, value: any) => {
						if (
							Number(value) >= 100 &&
							form.getFieldValue("cost_price") > 0
						) {
							return Promise.reject(
								new Error(
									"Invalid Entry. Margin cannot be 100% unless cost price is 0"
								)
							);
						}
						return Promise.resolve();
					}
				}
			]
		},
		{
			key: "sell_price",
			label: "Selling Price",
			hasFeedback: true,
			required: true,
			message: "Please enter selling price",
			widget: EWidgets.INPUTNUMBER,
			widgetProps: {
				width: "full",
				placeholder: "Please enter selling price",
				precision: 2,
				step: 0.01,
				onChange: onSellPriceChange,
				addOnAfter: getCurrencySettings().currency
			}
		},
		{
			key: "setup_price",
			label: "Setup Fee",
			hasFeedback: true,
			required: true,
			message: "Please enter setup fee",
			widget: EWidgets.INPUTNUMBER,
			widgetProps: {
				width: "full",
				placeholder: "Please enter setup fee",
				precision: 2,
				step: 0.01,
				min: 0,
				onChange: onSetupPriceChange,
				addOnAfter: getCurrencySettings().currency
			}
		},
		{
			key: "supplier_id",
			label: "Supplier Name",
			hasFeedback: true,
			widget: ({ value, onChange }: ICustomFormItemProps) => {
				return (
					<ZSupplierSelect
						onChange={onChange}
						placeholder="Please enter supplier name"
						defaultOptions={suppliers}
						value={value}
						dropdownRender={menu => (
							<div>
								{menu}
								<div
									className="add-entity-action"
									onMouseDown={$event => {
										$event.preventDefault();
									}}
									onClick={showAddSupplierDrawer}
								>
									Add Supplier
								</div>
							</div>
						)}
					/>
				);
			}
		},
		{
			key: "vendor_sku_no",
			label: "Supplier SKU No.",
			hasFeedback: true,
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter supplier SKU No"
			}
		},
		{
			key: "asin",
			label: "ASIN",
			hasFeedback: true,
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter ASIN"
			}
		}
	];

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			meta: {
				formItemLayout: [5, 19],
				fields: formFields
			}
		}
	];

	const formProps: IFormProps = {
		onFinish: isEditMode ? handleEditSubmit : handleAddSubmit,
		key: "ProductPricingPlanForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: onClose,
		isRightFooter: true,
		shouldDisableBeforeTouch: false,
		initialValues: {
			name: isEditMode
				? currentActiveProductPricingPlan?.name ?? ""
				: "Default Pricing",
			description: currentActiveProductPricingPlan?.description ?? "",
			billing_period: isEditMode
				? currentActiveProductPricingPlan?.billing_period ?? ""
				: ProductBillingType.OneTime,
			cost_price:
				currentActiveProductPricingPlan?.cost_price &&
				currentActiveProductPricingPlan?.cost_price > 0
					? currentActiveProductPricingPlan?.cost_price
					: 0,
			markup: isEditMode
				? currentActiveProductPricingPlan?.sell_price &&
				  currentActiveProductPricingPlan?.sell_price > 0 &&
				  populateMarkupMargin(
						currentActiveProductPricingPlan?.sell_price,
						true
				  )?.markup
				: defaultPreferences?.markup ?? 0,
			margin: isEditMode
				? currentActiveProductPricingPlan?.sell_price &&
				  currentActiveProductPricingPlan?.sell_price > 0 &&
				  populateMarkupMargin(
						currentActiveProductPricingPlan?.sell_price,
						true
				  )?.margin
				: defaultPreferences?.margin ?? 0,
			sell_price:
				currentActiveProductPricingPlan?.sell_price &&
				currentActiveProductPricingPlan?.sell_price > 0
					? currentActiveProductPricingPlan?.sell_price
					: 0,
			setup_price:
				currentActiveProductPricingPlan?.setup_price &&
				currentActiveProductPricingPlan?.setup_price > 0
					? currentActiveProductPricingPlan?.setup_price
					: 0,
			supplier_id:
				currentActiveProductPricingPlan?.supplier_id ?? undefined,
			vendor_name: currentActiveProductPricingPlan?.vendor_name ?? "",
			vendor_sku_no: currentActiveProductPricingPlan?.vendor_sku_no ?? ""
		}
	};

	return (
		<>
			<ZDrawer
				visible={visible}
				closable={true}
				destroyOnClose={true}
				onClose={onClose}
				title={`${isEditMode ? "Edit" : "Add"} Pricing Plan`}
				removeDefaultPadding
			>
				<CatalogCurrencyHint className="mx-4 mt-4" />
				{!isEditMode && <ZForm {...formProps} className="py-4" />}
				{isEditMode && (
					<Skeleton
						loading={!currentActiveProductPricingPlan}
						active
						paragraph={{ rows: 12 }}
					>
						<ZForm {...formProps} className="py-4" />
					</Skeleton>
				)}
			</ZDrawer>
		</>
	);
};

export default PricingPlanDrawer;
